import { useId, useState } from 'react';

import { isLiabilityAccountType, type TransferPurpose } from '@pfo/domain';

import { instantForTransactionDate, todayInputValue } from '../../../shared/lib/dates.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { formatVnd } from '../../../shared/lib/money.js';
import forms from '../../../shared/styles/forms.module.css';
import { MoneyInput } from '../../../shared/ui/MoneyInput.js';
import {
  buildSplitPayload,
  postSplit,
  updateEntry,
  type QuickAddOptions,
  type SplitAllocationDraft,
  type SubjectContext,
} from '../data/quickAddRepository.js';
import { TRANSFER_PURPOSE_LABELS } from '../model/transactionLabels.js';
import { ChipRow } from './ChipRow.js';
import { DatePicker } from './DatePicker.js';
import { missingFieldsMessage } from '../model/missingFields.js';
import { MissingHint } from './MissingHint.js';
import styles from './QuickAddControls.module.css';

/** One row of the form. `key` only exists so React can tell two empty rows apart. */
type AllocationRow = {
  key: number;
  kind: 'transfer' | 'expense';
  targetId: string;
  amount: number | null;
  purpose: TransferPurpose | '';
};

export type SplitFormInitial = Readonly<{
  sourceAccountId: string;
  date: string;
  note: string;
  allocations: ReadonlyArray<{
    kind: 'transfer' | 'expense';
    targetId: string;
    amount: number;
    purpose?: string | null;
  }>;
}>;

let nextKey = 0;
function emptyRow(kind: AllocationRow['kind'] = 'transfer'): AllocationRow {
  return { key: nextKey++, kind, targetId: '', amount: null, purpose: '' };
}

/**
 * "Chia tiền" — the salary arrives and goes five ways in ONE entry (spec v2 8.2 shape 4).
 *
 * Recording it as five separate transfers would count the income twice: each leg would look
 * like money arriving somewhere while the salary that funded them all is still on the books
 * (spec 11). So the source account is credited once here, for the total, and every
 * destination is a line of the same entry.
 *
 * A destination is either another of our accounts or an expense paid straight out of the
 * money — paying the insurance the moment the salary lands is the case the family actually
 * has, and it does not become a separate transaction.
 */
export function SplitForm({
  options,
  subjects,
  timeZone,
  entryId,
  initial,
  onSaved,
  onCancel,
}: {
  options: QuickAddOptions;
  subjects: SubjectContext;
  timeZone: string;
  entryId?: string;
  initial?: SplitFormInitial;
  onSaved: (message: string) => void;
  onCancel?: () => void;
}) {
  const noteId = useId();

  // A split credits an asset account; a card or a loan cannot fund one.
  const assetAccounts = options.accounts.filter(
    (account) => !isLiabilityAccountType(account.accountType),
  );

  const [source, setSource] = useState(initial?.sourceAccountId ?? options.defaultAccountId ?? '');
  const [date, setDate] = useState(initial?.date ?? (() => todayInputValue(timeZone)));
  const [note, setNote] = useState(initial?.note ?? '');
  const [rows, setRows] = useState<AllocationRow[]>(() =>
    initial === undefined
      ? [emptyRow(), emptyRow()]
      : initial.allocations.map((allocation) => ({
          key: nextKey++,
          kind: allocation.kind,
          targetId: allocation.targetId,
          amount: allocation.amount,
          purpose: purposeOf(allocation.purpose) ?? '',
        })),
  );

  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  function updateRow(key: number, patch: Partial<AllocationRow>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  const filledRows = rows.filter(
    (row) => row.targetId !== '' && row.amount !== null && row.amount > 0,
  );
  // Running total for the person reading the screen. The amount that reaches the ledger is
  // the one the builder computes from the same legs (DBD-014 — this is display only).
  const total = filledRows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
  const canSave = source !== '' && filledRows.length >= 2 && filledRows.length === rows.length;

  async function handleSubmit() {
    if (!canSave) return;

    setSaving(true);
    setMessage(null);
    try {
      const draft = {
        sourceAccountId: source,
        occurredAt: instantForTransactionDate(date, timeZone),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        allocations: filledRows.map((row): SplitAllocationDraft =>
          row.kind === 'transfer'
            ? {
                kind: 'transfer',
                toAccountId: row.targetId,
                amount: row.amount!,
                ...(row.purpose === '' ? {} : { purpose: row.purpose }),
              }
            : { kind: 'expense', categoryId: row.targetId, amount: row.amount! },
        ),
      };

      if (entryId === undefined) {
        await postSplit(options, subjects, draft);
        onSaved('Đã lưu giao dịch chia tiền.');
      } else {
        await updateEntry(entryId, buildSplitPayload(options, subjects, draft));
        onSaved('Đã cập nhật giao dịch chia tiền.');
      }
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <ChipRow
        label="Chia từ tài khoản"
        items={assetAccounts.map((account) => ({
          id: account.id,
          label: account.name,
          accountType: account.accountType,
        }))}
        selectedId={source}
        onSelect={setSource}
        emptyMessage="Chưa có tài khoản nào — thêm ở màn Tài sản."
      />

      <section className={styles.section}>
        <p className={styles.sectionLabel}>Chia thành</p>

        {rows.map((row, index) => (
          <AllocationFields
            key={row.key}
            row={row}
            index={index}
            options={options}
            sourceAccountId={source}
            canRemove={rows.length > 2}
            onChange={(patch) => updateRow(row.key, patch)}
            onRemove={() => setRows((current) => current.filter((item) => item.key !== row.key))}
          />
        ))}

        <button
          type="button"
          className={forms.secondary}
          onClick={() => setRows((current) => [...current, emptyRow()])}
        >
          Thêm khoản
        </button>

        {/* The total is the amount that will leave the source account — shown before saving
            because a split is the one entry where nobody can add it up in their head. */}
        <p className={styles.splitTotal} aria-live="polite">
          <span>Tổng trừ vào tài khoản</span>
          <strong>{formatVnd(total)}</strong>
        </p>
      </section>

      <DatePicker value={date} onChange={setDate} timeZone={timeZone} />

      <details className={forms.advanced}>
        <summary className={forms.summary}>Chi tiết</summary>

        <div className={forms.field}>
          <label htmlFor={noteId}>Ghi chú</label>
          <input
            id={noteId}
            className={forms.input}
            type="text"
            value={note}
            autoComplete="off"
            placeholder="Ví dụ: chia lương tháng 8"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </details>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={styles.footer}>
        {onCancel ? (
          <button type="button" className={forms.secondary} onClick={onCancel}>
            Hủy
          </button>
        ) : null}
        <button type="submit" className={styles.save} disabled={!canSave || isSaving}>
          {isSaving ? 'Đang lưu…' : entryId === undefined ? 'Lưu giao dịch' : 'Cập nhật'}
        </button>
      </div>
      <MissingHint
        message={missingFieldsMessage([
          source === '' && 'tài khoản nguồn',
          filledRows.length < 2 && 'ít nhất hai khoản phân bổ',
          filledRows.length !== rows.length && 'điền đủ các dòng',
        ])}
      />
    </form>
  );
}

/** One destination: what it is, where it goes, how much. */
function AllocationFields({
  row,
  index,
  options,
  sourceAccountId,
  canRemove,
  onChange,
  onRemove,
}: {
  row: AllocationRow;
  index: number;
  options: QuickAddOptions;
  sourceAccountId: string;
  canRemove: boolean;
  onChange: (patch: Partial<AllocationRow>) => void;
  onRemove: () => void;
}) {
  const targetId = useId();
  const amountId = useId();
  const purposeId = useId();

  const destinations =
    row.kind === 'transfer'
      ? options.accounts
          .filter((account) => !isLiabilityAccountType(account.accountType))
          .filter((account) => account.id !== sourceAccountId)
          .map((account) => ({ id: account.id, name: account.name }))
      : options.categories.map((category) => ({ id: category.id, name: category.name }));

  return (
    // Named so each row is addressable on its own: three rows repeat the same field labels,
    // and "Số tiền" on its own means nothing to a screen reader here.
    <div className={styles.allocation} role="group" aria-label={`Khoản ${index + 1}`}>
      <div className={styles.allocationHead}>
        <span className={styles.allocationIndex}>Khoản {index + 1}</span>
        {canRemove ? (
          <button type="button" className={styles.allocationRemove} onClick={onRemove}>
            Bỏ
          </button>
        ) : null}
      </div>

      <div className={styles.allocationKind} role="group" aria-label={`Loại khoản ${index + 1}`}>
        {(
          [
            ['transfer', 'Sang tài khoản'],
            ['expense', 'Trả luôn'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={row.kind === value ? styles.chipActive : styles.chip}
            aria-pressed={row.kind === value}
            // Changing the kind changes what the destination list means, so the old choice
            // cannot survive it.
            onClick={() => onChange({ kind: value, targetId: '', purpose: '' })}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={forms.field}>
        <label htmlFor={targetId}>{row.kind === 'transfer' ? 'Đến tài khoản' : 'Danh mục'}</label>
        <select
          id={targetId}
          className={forms.input}
          value={row.targetId}
          onChange={(event) => onChange({ targetId: event.target.value })}
        >
          <option value="">Chọn…</option>
          {destinations.map((destination) => (
            <option key={destination.id} value={destination.id}>
              {destination.name}
            </option>
          ))}
        </select>
      </div>

      <div className={forms.field}>
        <label htmlFor={amountId}>Số tiền</label>
        <MoneyInput id={amountId} value={row.amount} onChange={(amount) => onChange({ amount })} />
      </div>

      {row.kind === 'transfer' ? (
        <div className={forms.field}>
          <label htmlFor={purposeId}>Mục đích</label>
          <select
            id={purposeId}
            className={forms.input}
            value={row.purpose}
            onChange={(event) => onChange({ purpose: event.target.value as TransferPurpose | '' })}
          >
            <option value="">Suy từ tài khoản nhận</option>
            {Object.entries(TRANSFER_PURPOSE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
}

/** The stored purpose is free text in the ledger; only a known one is offered back. */
function purposeOf(value: string | null | undefined): TransferPurpose | null {
  if (value === null || value === undefined) return null;
  return value in TRANSFER_PURPOSE_LABELS ? (value as TransferPurpose) : null;
}
